import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { 
  Search, 
  ChevronDown, 
  ChevronUp, 
  HelpCircle, 
  MessageCircle, 
  Calendar,
  Shield,
  Zap,
  DollarSign,
  Settings,
  Users
} from "lucide-react";

const faqCategories = [
  {
    id: "general",
    title: "General Questions",
    icon: <HelpCircle className="w-5 h-5" />,
    faqs: [
      {
        question: "What is SelfSync and how does it work?",
        answer: "SelfSync is an AI-powered talent assessment platform that uses advanced machine learning to evaluate candidates with 99% accuracy. Our platform analyzes personality traits, behavioral patterns, and role fit through scientifically-designed assessments, helping you make data-driven hiring decisions in minutes instead of weeks."
      },
      {
        question: "How accurate are SelfSync's AI assessments?",
        answer: "Our AI assessments achieve 99% accuracy in predicting job performance and cultural fit. This high accuracy is achieved through advanced machine learning algorithms trained on millions of data points, continuous model refinement, and scientifically-validated assessment methodologies developed by industrial psychologists."
      },
      {
        question: "What types of assessments does SelfSync offer?",
        answer: "We offer comprehensive personality assessments, behavioral analysis, cognitive ability tests, role-specific evaluations, team compatibility analysis, and leadership potential assessments. All assessments are AI-powered and provide instant, detailed reports with actionable insights."
      },
      {
        question: "How long does it take to complete an assessment?",
        answer: "Most assessments take 30-45 minutes to complete. Our AI-optimized questions are designed to gather maximum insights efficiently, respecting both candidates' and hiring managers' time while maintaining assessment accuracy and depth."
      },
      // {
      //   question: "Can candidates retake assessments?",
      //   answer: "Retake policies depend on your organization's settings. Generally, we recommend allowing one retake if there were technical issues. However, for assessment integrity, multiple attempts on the same assessment type are typically not permitted within a 6-month period."
      // }
    ]
  },
  {
    id: "pricing",
    title: "Pricing & Plans",
    icon: <DollarSign className="w-5 h-5" />,
    faqs: [
      {
        question: "How much does SelfSync cost?",
        answer: "SelfSync offers flexible pricing starting with 2 free assessments. Paid plans begin at $49 for 10 assessments ($4.90 per assessment) and scale down to $3.99 per assessment for higher volumes. Enterprise plans with unlimited assessments are available with custom pricing."
      },
      {
        question: "Is there a free trial available?",
        answer: "Yes! Every new account includes 2 free assessment credits - no credit card required. This allows you to experience our platform's capabilities and see the quality of insights before committing to a paid plan."
      },
      {
        question: "What's included in each plan?",
        answer: "All plans include 99% accurate AI assessments, instant detailed reports, email support, and mobile optimization. Higher-tier plans add advanced analytics, priority support, bulk upload features, custom integrations, and dedicated account management."
      },
      // {
      //   question: "Can I cancel my subscription anytime?",
      //   answer: "Absolutely! All our plans are flexible with no long-term contracts. You can cancel anytime, and any unused assessment credits remain available until your current billing period ends. We believe in earning your business through value, not lock-in contracts."
      // },
      {
        question: "Do you offer volume discounts?",
        answer: "Yes, our per-assessment cost decreases significantly with volume. For example, it's $4.90 per assessment for 10 credits but only $3.99 per assessment for 200 credits. Enterprise customers with 1000+ assessments get custom pricing with additional savings."
      }
    ]
  },
  {
    id: "technology",
    title: "AI Technology",
    icon: <Zap className="w-5 h-5" />,
    faqs: [
      {
        question: "How does SelfSync's AI technology work?",
        answer: "Our AI uses advanced machine learning algorithms trained on behavioral data from millions of professionals. It analyzes response patterns, linguistic cues, and decision-making preferences to create comprehensive personality profiles and predict job performance with 99% accuracy."
      },
      {
        question: "What makes SelfSync's AI different from other assessment tools?",
        answer: "Unlike traditional assessments, our AI uses real-life scenario-based questions, adapts to individual responses, and provides dynamic role-fit recommendations. Our technology continuously learns and improves, ensuring assessments stay current with evolving workplace dynamics."
      },
      {
        question: "How do you ensure AI bias-free assessments?",
        answer: "We employ multiple bias-detection algorithms, diverse training datasets, and regular audits by industrial psychologists. Our AI is designed to focus purely on job-relevant traits and behaviors, eliminating demographic, cultural, or background biases that can affect traditional hiring."
      },
      {
        question: "Can the AI assess candidates for any role?",
        answer: "Yes, our AI is versatile and can assess candidates for roles across all industries and levels - from entry-level positions to C-suite executives. The AI adapts its evaluation criteria based on role requirements, company culture, and specific job competencies."
      },
      {
        question: "How often is the AI model updated?",
        answer: "Our AI models are continuously updated with new data and improved algorithms. Major model updates occur quarterly, with incremental improvements deployed monthly. This ensures our 99% accuracy rate is maintained and improved over time."
      }
    ]
  },
  {
    id: "implementation",
    title: "Implementation & Setup",
    icon: <Settings className="w-5 h-5" />,
    faqs: [
      {
        question: "How quickly can we get started with SelfSync?",
        answer: "You can start using SelfSync in under 30 minutes! Simply sign up, add your team members, and begin sending assessments immediately. Our platform is designed for instant deployment with no complex setup or technical expertise required."
      },
      {
        question: "Do you provide implementation support?",
        answer: "Yes! All paid plans include email support, and higher-tier plans include dedicated onboarding calls, implementation guidance, and ongoing account management. Our customer success team ensures you maximize ROI from day one."
      },
      // {
      //   question: "Can SelfSync integrate with our existing HR systems?",
      //   answer: "Absolutely! We offer integrations with popular ATS systems, HRIS platforms, and recruiting tools. API access is available for custom integrations. Our team can help you seamlessly connect SelfSync with your existing workflow."
      // },
      {
        question: "What training do our team members need?",
        answer: "Minimal training required! Our platform is intuitive and user-friendly. We provide quick start guides, video tutorials, and best practices documentation. Most users are fully proficient within their first day of use."
      },
      {
        question: "Can we customize assessments for our company?",
        answer: "Yes, higher-tier plans allow assessment customization including company-specific questions, branding, custom competency models, and role-specific evaluation criteria. Enterprise plans include fully white-labeled solutions."
      }
    ]
  },
  {
    id: "security",
    title: "Security & Privacy",
    icon: <Shield className="w-5 h-5" />,
    faqs: [
      {
        question: "How secure is candidate data on SelfSync?",
        answer: "We employ bank-level security with SSL/TLS encryption, SOC 2 compliance, and GDPR adherence. All data is encrypted in transit and at rest, with regular security audits and penetration testing to ensure maximum protection."
      },
      {
        question: "Who has access to assessment results?",
        answer: "Only authorized personnel in your organization can access results. You control user permissions and can restrict access by role, department, or specific assessments. Candidates can request their own results, and all access is logged for audit purposes."
      },
      {
        question: "How long do you retain candidate data?",
        answer: "Data retention follows your organizational policies and legal requirements. By default, we retain data for 3 years, but this can be customized. You can request data deletion at any time, and we comply with 'right to be forgotten' requests per GDPR guidelines."
      },
      {
        question: "Is SelfSync GDPR compliant?",
        answer: "Yes, we are fully GDPR compliant with comprehensive data protection measures, clear privacy policies, consent management, and data subject rights support. We also comply with CCPA, SOC 2, and other international privacy regulations."
      },
      // {
      //   question: "Can candidates see their assessment results?",
      //   answer: "Yes, if enabled by your organization. We believe in transparency and candidates can receive their personality insights and development recommendations. This creates a positive candidate experience even if they're not selected for the role."
      // }
    ]
  }
];

function FAQSchema({ faqs }) {
  if (!faqs || faqs.length === 0) return null;

  const allFaqs = faqs.flatMap(category => category.faqs);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
    </Helmet>
  );
}

export default function FAQPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("general");
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredFaqs, setFilteredFaqs] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const allFaqs = faqCategories.flatMap(category => 
        category.faqs.map(faq => ({ ...faq, category: category.title }))
      );
      const filtered = allFaqs.filter(faq => 
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredFaqs(filtered);
    } else {
      setFilteredFaqs([]);
    }
  }, [searchTerm]);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const currentCategory = faqCategories.find(cat => cat.id === activeCategory);
  const displayFaqs = searchTerm ? filteredFaqs : currentCategory?.faqs || [];

  return (
  <>
  <Helmet>
        <title>FAQ - Frequently Asked Questions | SelfSync AI Hiring Platform</title>
        <meta
          name="description"
          content="Find answers to common questions about SelfSync's AI hiring platform. Learn about pricing, security, implementation, and our 99% accurate talent assessments."
        />
        <meta name="keywords" content="SelfSync FAQ, AI hiring questions, talent assessment help, pricing questions, implementation guide" />
        <link rel="canonical" href="https://selfsync.ai/faq" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="FAQ - Frequently Asked Questions | SelfSync AI Hiring Platform" />
        <meta property="og:description" content="Find answers to common questions about SelfSync's AI hiring platform, pricing, security, and implementation." />
        <meta property="og:url" content="https://selfsync.ai/faq" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://selfsync.ai/static/media/SelfSync%20Logo.df7570a5489657df44eb.png" />
      </Helmet>

      <FAQSchema faqs={faqCategories} />

      <div className="bg-white min-h-screen">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-purple-600 to-cyan-600 text-white py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4"
            >
              <span className="bg-yellow-400 text-purple-900 px-4 py-2 rounded-full text-sm font-bold">
                💡 Get Instant Answers
              </span>
            </motion.div>
            
        <motion.h1
              initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold mb-6"
        >
              Frequently Asked
              <br />
              <span className="text-yellow-400">Questions</span>
        </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl mb-8"
            >
              Everything you need to know about SelfSync's AI hiring platform
            </motion.p>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="relative max-w-2xl mx-auto"
            >
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-2 md:py-3 rounded-lg text-gray-900 text-lg border-0 focus:ring-4 focus:ring-white/20 shadow-lg"
              />
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-4 gap-8">
              
              {/* Categories Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Categories</h2>
                  <div className="space-y-2">
                    {faqCategories.map((category) => (
                      <button
                        key={category.id}
            onClick={() => {
                          setActiveCategory(category.id);
                          setSearchTerm("");
                          setOpenIndex(null);
                        }}
                        className={`w-full text-left p-4 rounded-lg font-medium transition-all flex items-center gap-3 ${
                          activeCategory === category.id
                            ? "bg-purple-600 text-white shadow-lg"
                            : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        <span className={activeCategory === category.id ? "text-yellow-400" : "text-purple-600"}>
                          {category.icon}
                        </span>
            {category.title}
                      </button>
                    ))}
                  </div>

                  {/* Contact Support */}
                  <div className="mt-8 p-6 bg-gradient-to-br from-purple-50 to-cyan-50 rounded-xl">
                    <MessageCircle className="w-8 h-8 text-purple-600 mb-4" />
                    <h3 className="font-bold text-gray-900 mb-2">Still have questions?</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Can't find what you're looking for? Our support team is here to help.
                    </p>
                    <button
                      onClick={() => navigate("/contact-us")}
                      className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors"
                    >
                      Contact Support
                    </button>
                  </div>
                </div>
              </div>

              {/* FAQ Content */}
              <div className="lg:col-span-3">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    {searchTerm ? `Search Results (${displayFaqs.length})` : currentCategory?.title}
                  </h2>
                  <p className="text-gray-600">
                    {searchTerm 
                      ? `Found ${displayFaqs.length} results for "${searchTerm}"`
                      : "Click on any question to see the detailed answer"
                    }
                  </p>
                </div>

                {displayFaqs.length === 0 ? (
                  <div className="text-center py-12">
                    <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      {searchTerm ? "No results found" : "No FAQs available"}
                    </h3>
                    <p className="text-gray-500">
                      {searchTerm 
                        ? "Try searching with different keywords or browse categories"
                        : "Please select a category to view questions"
                      }
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {displayFaqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
              key={index}
                          className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow"
            >
              <button
                            className="w-full text-left p-4 flex justify-between items-start gap-4"
                onClick={() => handleToggle(index)}
              >
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900 text-lg mb-1">
                {faq.question}
                              </h3>
                              {searchTerm && faq.category && (
                                <span className="text-sm text-purple-600 font-medium">
                                  {faq.category}
                                </span>
                              )}
                            </div>
                            <div className="flex-shrink-0">
                {isOpen ? (
                                <ChevronUp className="w-6 h-6 text-purple-600" />
                              ) : (
                                <ChevronDown className="w-6 h-6 text-gray-400" />
                              )}
                            </div>
                          </button>

                          {isOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                              className="border-t border-gray-100 px-6 pb-6"
                            >
                              <div className="pt-4 text-gray-700 leading-relaxed">
                                {faq.answer}
                              </div>
                            </motion.div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-11 ">
          <div className="max-w-4xl mx-auto px-6 text-center">
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Ready to Experience 99% Accurate AI Hiring?
              </h2>
              <p className="text-md md:text-xl text-gray-600 mb-8">
                Start with 2 free assessments - no credit card required
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => window.open("https://portal.selfsync.ai/pricing", "_blank")}
                  className="bg-gradient-to-r from-purple-600 to-cyan-600 text-white px-8 py-3 rounded-lg font-bold hover:from-purple-700 hover:to-cyan-700 transition-colors shadow-lg flex items-center justify-center gap-2"
                >
                  <Users className="w-5 h-5" />
                  Start Free Trial
                </button>
                <button
                  onClick={() => navigate("/schedule-demo")}
                  className="border-2 border-purple-600 text-purple-600 px-8 py-3 rounded-lg font-bold hover:bg-purple-600 hover:text-white transition-colors flex items-center justify-center gap-2"
                >
                  <Calendar className="w-5 h-5" />
                  Schedule Demo
                </button>
              </div>

              <p className="text-sm text-gray-500 mt-6">
                Join 260+ companies using AI to hire with confidence
              </p>
            </motion.div> */}
          </div>
        </section>
    </div>
  </>
  );
}
